import {
  Color,
  DoubleSide,
  Matrix4,
  MeshBasicMaterial,
  NoBlending,
  RedFormat,
  RGBAFormat,
  ShaderMaterial,
  SRGBColorSpace,
  UniformsUtils,
  Vector2,
  Vector3,
  WebGLRenderTarget,
} from '@seddi/three';
import { FullScreenQuad, Pass } from '@seddi/three/examples/jsm/postprocessing/Pass.js';
import { CopyShader } from '@seddi/three/examples/jsm/shaders/CopyShader.js';

export class ThreeOutlinePass extends Pass {
  constructor(renderer, scene, camera) {
    super();

    this.scene = scene;
    this.camera = camera;
    this.selectedObjects = [];

    this.visibleEdgeColor = new Color(0x4e0fd2).convertSRGBToLinear();
    // this.hiddenEdgeColor = this.visibleEdgeColor.clone();
    this.edgeGlow = 0.0;
    // this.usePatternTexture = false;
    this.edgeThickness = 0.1;
    this.edgeStrength = 1.0;
    this.downSampleRatio = 2;

    const resolution = renderer.getSize(new Vector2());
    const resx = Math.round(resolution.x / this.downSampleRatio);
    const resy = Math.round(resolution.y / this.downSampleRatio);
    this.maskBufferMaterial = new MeshBasicMaterial({ color: 0xffffff });
    this.maskBufferMaterial.side = DoubleSide;
    this.renderTargetMaskBuffer = new WebGLRenderTarget(resolution.x, resolution.y, {
      depthBuffer: false,
      format: RedFormat,
    });
    this.renderTargetMaskBuffer.texture.name = 'OutlinePass.mask';
    // this.depthMaterial = new MeshDepthMaterial();
    // this.depthMaterial.side = DoubleSide;
    // this.depthMaterial.depthPacking = RGBADepthPacking;
    // this.depthMaterial.blending = NoBlending;
    this.prepareMaskMaterial = this.getPrepareMaskMaterial();
    // this.renderTargetDepthBuffer = new WebGLRenderTarget(resolution.x, resolution.y, pars);
    // this.renderTargetDepthBuffer.texture.name = 'OutlinePass.depth';
    this.renderTargetMaskDownSampleBuffer = new WebGLRenderTarget(resx, resy, {
      depthBuffer: false,
      format: RedFormat,
    });
    this.renderTargetMaskDownSampleBuffer.texture.name = 'OutlinePass.depthDownSample';
    this.renderTargetBlurBuffer1 = new WebGLRenderTarget(resx, resy, {
      colorSpace: SRGBColorSpace,
      depthBuffer: false,
      format: RGBAFormat,
    });
    this.renderTargetBlurBuffer1.texture.name = 'OutlinePass.blur1';
    // this.renderTargetBlurBuffer2 = new WebGLRenderTarget(
    //   Math.round(resx / 2),
    //   Math.round(resy / 2),
    //   pars,
    // );
    // this.renderTargetBlurBuffer2.texture.name = 'OutlinePass.blur2';
    this.edgeDetectionMaterial = this.getEdgeDetectionMaterial();
    this.renderTargetEdgeBuffer1 = new WebGLRenderTarget(resx, resy, {
      colorSpace: SRGBColorSpace,
      depthBuffer: false,
      format: RGBAFormat,
    });
    this.renderTargetEdgeBuffer1.texture.name = 'OutlinePass.edge1';
    // this.renderTargetEdgeBuffer2 = new WebGLRenderTarget(
    //   Math.round(resx / 2),
    //   Math.round(resy / 2),
    //   pars,
    // );
    // this.renderTargetEdgeBuffer2.texture.name = 'OutlinePass.edge2';
    const MAX_EDGE_THICKNESS = 4;
    this.separableBlurMaterial1 = this.getSeperableBlurMaterial(MAX_EDGE_THICKNESS);
    this.separableBlurMaterial1.uniforms.kernelRadius.value = 1;
    this.copyMaterial = this.getCopyMaterial();
    this.overlayMaterial = this.getOverlayMaterial();

    this.fsQuad = new FullScreenQuad(null);
    this.clearColor = new Color();
    this.textureMatrix = new Matrix4();
  }

  dispose() {
    this.renderTargetMaskBuffer.dispose();
    // this.renderTargetDepthBuffer.dispose();
    this.renderTargetMaskDownSampleBuffer.dispose();
    this.renderTargetBlurBuffer1.dispose();
    this.renderTargetEdgeBuffer1.dispose();
    // this.renderTargetBlurBuffer2.dispose();
    // this.renderTargetEdgeBuffer2.dispose();

    super.dispose();
  }

  setSize(width, height) {
    this.renderTargetMaskBuffer.setSize(width, height);
    // this.renderTargetDepthBuffer.setSize(width, height);
    let resx = Math.round(width / this.downSampleRatio);
    let resy = Math.round(height / this.downSampleRatio);
    this.renderTargetMaskDownSampleBuffer.setSize(resx, resy);
    this.renderTargetBlurBuffer1.setSize(resx, resy);
    this.renderTargetEdgeBuffer1.setSize(resx, resy);
    this.edgeDetectionMaterial.uniforms.texSize.value.set(resx, resy);
    this.separableBlurMaterial1.uniforms.texSize.value.set(resx, resy);
    // resx = Math.round(resx / 2);
    // resy = Math.round(resy / 2);
    // this.renderTargetBlurBuffer2.setSize(resx, resy);
    // this.renderTargetEdgeBuffer2.setSize(resx, resy);
    // this.separableBlurMaterial2.uniforms.texSize.value.set(resx, resy);
  }

  changeVisibilityOfSelectedObjects(bVisible) {
    const { selectedObjects } = this;
    selectedObjects.forEach((selectedObject) =>
      selectedObject.traverse((object) => {
        if (object.isMesh || object.isLine || object.isLineSegments || object.isSprite) {
          if (bVisible) {
            object.visible = object.userData.oldVisible;
            delete object.userData.oldVisible;
          } else {
            object.userData.oldVisible = object.visible;
            object.visible = bVisible;
          }
        }
      }),
    );
  }

  changeVisibilityOfNonSelectedObjects(bVisible) {
    const { scene, selectedObjects } = this;
    const selectedMeshes = new Map();
    selectedObjects.forEach((selectedObject) =>
      selectedObject.traverse((object) => {
        if (object.isMesh || object.isLine || object.isLineSegments || object.isSprite) {
          selectedMeshes.set(object.id, true);
        }
      }),
    );
    scene.traverse((object) => {
      if (
        !selectedMeshes.has(object.id) &&
        (object.isMesh || object.isLine || object.isLineSegments || object.isSprite)
      ) {
        const visibility = object.visible;
        if (!bVisible || object.bVisible) {
          object.visible = bVisible;
        }
        object.bVisible = visibility;
      }
    });
  }

  updateTextureMatrix() {
    this.textureMatrix.set(0.5, 0.0, 0.0, 0.5, 0.0, 0.5, 0.0, 0.5, 0.0, 0.0, 0.5, 0.5, 0.0, 0.0, 0.0, 1.0);
    this.textureMatrix.multiply(this.camera.projectionMatrix);
    this.textureMatrix.multiply(this.camera.matrixWorldInverse);
  }

  render(renderer, writeBuffer, readBuffer) {
    const {
      separableBlurMaterial1,
      camera,
      copyMaterial,
      // depthMaterial,
      edgeDetectionMaterial,
      edgeStrength,
      edgeThickness,
      fsQuad,
      // hiddenEdgeColor,
      clearColor,
      overlayMaterial,
      // patternTexture,
      prepareMaskMaterial,
      renderTargetBlurBuffer1,
      // renderTargetDepthBuffer,
      renderTargetEdgeBuffer1,
      renderTargetMaskBuffer,
      renderTargetMaskDownSampleBuffer,
      renderToScreen,
      scene,
      selectedObjects,
      textureMatrix,
      // usePatternTexture,
      visibleEdgeColor,
    } = this;

    if (selectedObjects.length > 0) {
      renderer.getClearColor(clearColor);
      const clearAlpha = renderer.getClearAlpha();
      const autoClear = renderer.autoClear;
      renderer.autoClear = false;
      renderer.setClearColor(0xffffff, 1);

      // Make selected objects invisible
      // this.changeVisibilityOfSelectedObjects(false);
      const background = scene.background;
      scene.background = null;

      // 1. Draw Non Selected objects in the depth buffer
      // scene.overrideMaterial = depthMaterial;
      // renderer.setRenderTarget(renderTargetDepthBuffer);
      // renderer.clear();
      // renderer.render(scene, camera);

      // Make selected objects visible
      // this.changeVisibilityOfSelectedObjects(true);

      // Update Texture Matrix for Depth compare
      this.updateTextureMatrix();

      // Make non selected objects invisible, and draw only the selected objects, by comparing the depth buffer of non selected objects
      this.changeVisibilityOfNonSelectedObjects(false);
      scene.overrideMaterial = prepareMaskMaterial;
      prepareMaskMaterial.uniforms.cameraNearFar.value = new Vector2(camera.near, camera.far);
      // prepareMaskMaterial.uniforms.depthTexture.value = renderTargetDepthBuffer.texture;
      prepareMaskMaterial.uniforms.textureMatrix.value = textureMatrix;
      renderer.setRenderTarget(renderTargetMaskBuffer);
      renderer.clear();
      renderer.render(scene, camera);
      scene.overrideMaterial = null;
      this.changeVisibilityOfNonSelectedObjects(true);

      // scene.background = background;

      // 2. Downsample to Half resolution
      fsQuad.material = copyMaterial;
      copyMaterial.uniforms.tDiffuse.value = renderTargetMaskBuffer.texture;
      renderer.setRenderTarget(renderTargetMaskDownSampleBuffer);
      fsQuad.render(renderer);

      // 3. Apply Edge Detection THREE.Pass
      fsQuad.material = edgeDetectionMaterial;
      edgeDetectionMaterial.uniforms.maskTexture.value = renderTargetMaskDownSampleBuffer.texture;
      edgeDetectionMaterial.uniforms.visibleEdgeColor.value = visibleEdgeColor;
      // edgeDetectionMaterial.uniforms.hiddenEdgeColor.value = hiddenEdgeColor;
      renderer.setRenderTarget(renderTargetEdgeBuffer1);
      fsQuad.render(renderer);

      // 4. Apply Blur on Half res
      fsQuad.material = separableBlurMaterial1;
      separableBlurMaterial1.uniforms.colorTexture.value = renderTargetEdgeBuffer1.texture;
      separableBlurMaterial1.uniforms.kernelRadius.value = edgeThickness;
      separableBlurMaterial1.uniforms.direction.value = ThreeOutlinePass.blurDirectionX;
      renderer.setRenderTarget(renderTargetBlurBuffer1);
      fsQuad.render(renderer);
      separableBlurMaterial1.uniforms.colorTexture.value = renderTargetBlurBuffer1.texture;
      separableBlurMaterial1.uniforms.direction.value = ThreeOutlinePass.blurDirectionY;
      renderer.setRenderTarget(renderTargetEdgeBuffer1);
      fsQuad.render(renderer);

      // Blend it additively over the input texture
      fsQuad.material = overlayMaterial;
      overlayMaterial.uniforms.baseTexture.value = readBuffer.texture;
      overlayMaterial.uniforms.edgeTexture.value = renderTargetEdgeBuffer1.texture;
      // overlayMaterial.uniforms.patternTexture.value = patternTexture;
      overlayMaterial.uniforms.edgeStrength.value = edgeStrength;
      // overlayMaterial.uniforms.usePatternTexture.value = usePatternTexture;

      if (renderToScreen) {
        renderer.setRenderTarget(null);
      } else {
        renderer.setRenderTarget(writeBuffer);
      }

      fsQuad.render(renderer);

      renderer.setClearColor(clearColor, clearAlpha);
      renderer.autoClear = autoClear;
    } else {
      // Pass-through.
      fsQuad.material = copyMaterial;
      copyMaterial.uniforms.tDiffuse.value = readBuffer.texture;

      if (renderToScreen) {
        renderer.setRenderTarget(null);
      } else {
        renderer.setRenderTarget(writeBuffer);
      }

      fsQuad.render(renderer);
    }
  }

  getPrepareMaskMaterial() {
    return new ShaderMaterial({
      uniforms: {
        // depthTexture: { value: null },
        cameraNearFar: { value: new Vector2(0.5, 0.5) },
        textureMatrix: { value: new Matrix4() },
      },
      vertexShader: [
        'varying vec4 projTexCoord;',
        'varying vec4 vPosition;',
        'uniform mat4 textureMatrix;',
        'void main() {',
        '  vPosition = modelViewMatrix * vec4( position, 1.0 );',
        '  vec4 worldPosition = modelMatrix * vec4( position, 1.0 );',
        '  projTexCoord = textureMatrix * worldPosition;',
        '  gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );',
        '}',
      ].join('\n'),
      fragmentShader: [
        '#include <packing>',
        'varying vec4 vPosition;',
        'varying vec4 projTexCoord;',
        // 'uniform sampler2D depthTexture;',
        'uniform vec2 cameraNearFar;',
        'void main() {',
        // '  float depth = unpackRGBAToDepth(texture2DProj( depthTexture, projTexCoord ));',
        // '  float viewZ = -perspectiveDepthToViewZ( depth, cameraNearFar.x, cameraNearFar.y );',
        // '  float depthTest = (-vPosition.z > viewZ) ? 1.0 : 0.0;',
        // '  gl_FragColor = vec4(0.0, depthTest, 1.0, 1.0);',
        '  gl_FragColor = vec4(0.0, 0.0, 0.0, 1.0);',
        '}',
      ].join('\n'),
      side: DoubleSide,
    });
  }

  getEdgeDetectionMaterial() {
    return new ShaderMaterial({
      uniforms: {
        maskTexture: { value: null },
        texSize: { value: new Vector2(0.5, 0.5) },
        visibleEdgeColor: { value: new Vector3(1.0, 1.0, 1.0) },
        // hiddenEdgeColor: { value: new Vector3(1.0, 1.0, 1.0) },
      },
      vertexShader: [
        'varying vec2 vUv;',
        'void main() {',
        '  vUv = uv;',
        '  gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );',
        '}',
      ].join('\n'),
      fragmentShader: [
        'varying vec2 vUv;',
        'uniform sampler2D maskTexture;',
        'uniform vec2 texSize;',
        'uniform vec3 visibleEdgeColor;',
        // 'uniform vec3 hiddenEdgeColor;',
        'void main() {',
        '  vec2 invSize = 1.0 / texSize;',
        '  vec4 uvOffset = vec4(1.0, 0.0, 0.0, 1.0) * vec4(invSize, invSize);',
        '  vec4 c1 = texture2D( maskTexture, vUv + uvOffset.xy);',
        '  vec4 c2 = texture2D( maskTexture, vUv - uvOffset.xy);',
        '  vec4 c3 = texture2D( maskTexture, vUv + uvOffset.zw);',
        '  vec4 c4 = texture2D( maskTexture, vUv - uvOffset.zw);',
        '  float diff1 = (c1.r - c2.r)*0.5;',
        '  float diff2 = (c3.r - c4.r)*0.5;',
        '  float d = length( vec2(diff1, diff2) );',
        // '  float a1 = min(c1.g, c2.g);',
        // '  float a2 = min(c3.g, c4.g);',
        // '  float visibilityFactor = min(a1, a2);',
        // '  vec3 edgeColor = 1.0 - visibilityFactor > 0.001 ? visibleEdgeColor : hiddenEdgeColor;',
        // '  gl_FragColor = vec4(edgeColor, 1.0) * vec4(d);',
        '  gl_FragColor = vec4(visibleEdgeColor, 1.0) * vec4(d);',
        '}',
      ].join('\n'),
      blending: NoBlending,
      depthTest: false,
      depthWrite: false,
    });
  }

  getSeperableBlurMaterial(maxRadius) {
    return new ShaderMaterial({
      defines: {
        MAX_RADIUS: maxRadius,
      },
      uniforms: {
        colorTexture: { value: null },
        texSize: { value: new Vector2(0.5, 0.5) },
        direction: { value: new Vector2(0.5, 0.5) },
        kernelRadius: { value: 1.0 },
      },
      vertexShader: [
        'varying vec2 vUv;',
        'void main() {',
        '  vUv = uv;',
        '  gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );',
        '}',
      ].join('\n'),
      fragmentShader: [
        '#include <common>',
        'varying vec2 vUv;',
        'uniform sampler2D colorTexture;',
        'uniform vec2 texSize;',
        'uniform vec2 direction;',
        'uniform float kernelRadius;',
        'float gaussianPdf(in float x, in float sigma) {',
        '  return 0.39894 * exp( -0.5 * x * x/( sigma * sigma))/sigma;',
        '}',
        'void main() {',
        '  vec2 invSize = 1.0 / texSize;',
        '  float weightSum = gaussianPdf(0.0, kernelRadius);',
        '  vec4 diffuseSum = texture2D( colorTexture, vUv) * weightSum;',
        '  vec2 delta = direction * invSize * kernelRadius/float(MAX_RADIUS);',
        '  vec2 uvOffset = delta;',
        '  for( int i = 1; i <= MAX_RADIUS; i ++ ) {',
        '    float w = gaussianPdf(uvOffset.x, kernelRadius);',
        '    vec4 sample1 = texture2D( colorTexture, vUv + uvOffset);',
        '    vec4 sample2 = texture2D( colorTexture, vUv - uvOffset);',
        '    diffuseSum += ((sample1 + sample2) * w);',
        '    weightSum += (2.0 * w);',
        '    uvOffset += delta;',
        '  }',
        '  gl_FragColor = diffuseSum/weightSum;',
        '}',
      ].join('\n'),
    });
  }

  getOverlayMaterial() {
    return new ShaderMaterial({
      uniforms: {
        baseTexture: { value: null },
        edgeTexture: { value: null },
        // maskTexture: { value: null },
        // patternTexture: { value: null },
        edgeStrength: { value: 1.0 },
        // usePatternTexture: { value: 0.0 },
      },
      vertexShader: [
        'varying vec2 vUv;',
        'void main() {',
        '  vUv = uv;',
        '  gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );',
        '}',
      ].join('\n'),
      fragmentShader: [
        'varying vec2 vUv;',
        'uniform sampler2D baseTexture;',
        'uniform sampler2D edgeTexture;',
        // 'uniform sampler2D patternTexture;',
        'uniform float edgeStrength;',
        // 'uniform bool usePatternTexture;',
        'void main() {',
        '  vec4 edgeValue = texture2D(edgeTexture, vUv);',
        '  vec4 baseValue = texture2D(baseTexture, vUv);',
        // '  vec4 maskColor = texture2D(maskTexture, vUv);',
        // '  vec4 patternColor = texture2D(patternTexture, 6.0 * vUv);',
        // '  float visibilityFactor = 1.0 - maskColor.g > 0.0 ? 1.0 : 0.5;',
        // '  vec4 finalColor = edgeStrength * maskColor.r * edgeValue;',
        // '  if (usePatternTexture) {',
        // '    finalColor += + visibilityFactor * (1.0 - maskColor.r) * (1.0 - patternColor.r);',
        // '  gl_FragColor = finalColor;',
        // '  gl_FragColor = vec4(edgeValue.xyz, 1.0);',
        // NOTE: Emulate hardware Additive-blending
        '  gl_FragColor = baseValue + edgeValue;',
        '}',
      ].join('\n'),
      // NOTE: Hardware Additive-blending is creating artifacts with the current setup.
      // blending: AdditiveBlending,
      blending: NoBlending,
      depthTest: false,
      depthWrite: false,
    });
  }

  getCopyMaterial() {
    return new ShaderMaterial({
      uniforms: {
        ...UniformsUtils.clone(CopyShader.uniforms),
        opacity: { value: 1.0 },
      },
      vertexShader: CopyShader.vertexShader,
      fragmentShader: CopyShader.fragmentShader,
      blending: NoBlending,
      depthTest: false,
      depthWrite: false,
      transparent: true,
    });
  }
}

ThreeOutlinePass.blurDirectionX = new Vector2(1.0, 0.0);
ThreeOutlinePass.blurDirectionY = new Vector2(0.0, 1.0);
